import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSellerData } from '../../hooks/useSellerData';
import { useAuth } from '../../context/AuthContext';
import { useMessage } from '../../context/MessageContext';
import Loading from '../../components/common/Loading';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';

// Import modular components
import StoreHeader from '../../components/store/StoreHeader';
import QuickStats from '../../components/store/QuickStats';
import QuickActions from '../../components/store/QuickActions';
import RecentListings from '../../components/store/RecentListings';
import PerformanceTips from '../../components/store/PerformanceTips';

export default function StoreDashboard() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  
  const {
    listings,
    orders,
    sellerStats,
    sellerProfile,
    loading,
    refreshing,
    fetchData,
  } = useSellerData();

  const [checkingSellerStatus, setCheckingSellerStatus] = useState(true);
  const [showLaterModal, setShowLaterModal] = useState(false);

  useEffect(() => {
    checkSellerStatus();
  }, [user]);

  const checkSellerStatus = async () => {
    if (!user) {
      setCheckingSellerStatus(false);
      return;
    }

    try {
      if (!user.is_seller) {
        setCheckingSellerStatus(false);
        return;
      }
      setCheckingSellerStatus(false);
    } catch (error) {
      //console.error('Error checking seller status:', error);
      setCheckingSellerStatus(false);
    }
  };

  const navigateToStoreSetup = () => {
    navigate('/store/setup-store');
  };

  const navigateToStoreProfile = () => {
    navigate('/store/settings');
  };

  /* ========== MOBILE VIEW ========== */
  const MobileView = () => {
    // Store Status Component
    const StoreStatusMobile = () => {
      if (!sellerProfile) return null;

      return (
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <h3 className="font-bold text-black mb-3">Store Status</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Store Status</span>
              <span className={`px-2 py-1 text-xs rounded-full ${
                sellerProfile.status === 'active' ? 'bg-green-100 text-green-800' :
                sellerProfile.status === 'suspended' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {sellerProfile.status || 'Unknown'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Approval Status</span>
              <span className={`px-2 py-1 text-xs rounded-full ${
                sellerProfile.is_approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
              }`}>
                {sellerProfile.is_approved ? 'Approved' : 'Pending Review'}
              </span>
            </div>
            
            {!sellerProfile.is_approved && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-800 text-sm">
                  ⏳ Your store is under review. You can still list items, but they won't be visible to buyers until approved.
                </p>
              </div>
            )}
          </div>
        </div>
      );
    };

    // Store Setup Prompt for Mobile
    const StoreSetupPromptMobile = () => (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-20 h-20 mx-auto bg-gradient-to-r from-orange-100 to-red-100 rounded-full flex items-center justify-center mb-6">
            <svg className="w-10 h-10 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          
          <h1 className="text-2xl font-bold text-black mb-4">
            Ready to Start Selling? 🛍️
          </h1>
          
          <p className="text-gray-600 mb-8">
            Set up your store in just a few minutes and start reaching buyers on our marketplace
          </p>
          
          <div className="space-y-4 mb-8">
            {[
              'Upload store logo & cover photo',
              'Create your store profile',
              'Set delivery options & fees',
              'Start listing your products'
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-3">
                <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-gray-700 text-left text-base">{item}</span>
              </div>
            ))}
          </div>

          <div className="space-y-3">
            <button
              onClick={navigateToStoreSetup}
              className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 px-6 rounded-xl font-semibold hover:from-orange-600 hover:to-red-600 transition flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Setup My Store
            </button>

            <button
              onClick={() => setShowLaterModal(true)}
              className="w-full py-3 text-gray-600 hover:text-gray-800 transition text-base"
            >
              I'll do this later
            </button>
          </div>
        </div>
      </div>
    );

    // User not logged in - Mobile
    if (!user) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-20 h-20 mx-auto bg-gradient-to-r from-orange-100 to-red-100 rounded-full flex items-center justify-center mb-6">
              <svg className="w-10 h-10 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            
            <h1 className="text-2xl font-bold text-black mb-4">
              Your Store Awaits! 🛍️
            </h1>
            
            <p className="text-gray-600 mb-8">
              Log in to access your store dashboard and start selling on our marketplace
            </p>
            
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 px-6 rounded-xl font-semibold hover:from-orange-600 hover:to-red-600 transition"
            >
              Log In to Your Store
            </button>
          </div>
        </div>
      );
    }

    // User is not a seller - Mobile
    if (!user.is_seller) {
      return (
        <>
          <StoreSetupPromptMobile />
          <MobileBottomNav />
          <ConfirmationModal
            isOpen={showLaterModal}
            onClose={() => setShowLaterModal(false)}
            onConfirm={() => {
              setShowLaterModal(false);
              navigate("/");
            }}
            title="Setup Later"
            message="You can setup your store anytime from your profile. Some features will be limited until you complete store setup."
            confirmText="OK"
            cancelText={null}
            showCancel={false}
          />
        </>
      );
    }

    return (
      <div className="lg:hidden min-h-screen bg-gray-50">
        {/* Mobile Header */}
        <Header />
        <header className="bg-white shadow-sm sticky top-0 z-50">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100"
              >
                <svg className="w-5 h-5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <h1 className="text-lg font-bold text-black">Store Dashboard</h1>
              
              <button
                onClick={() => navigate('/store/settings')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <svg className="w-5 h-5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            </div>
          </div>
        </header>

        <div className="px-4 py-4">
          {/* Store Header with Mobile prop */}
          <StoreHeader 
          sellerProfile={sellerProfile}
          onNavigateToProfile={navigateToStoreProfile}
          onNavigateToChats={() => navigate('/chats')}
          onShareStore={() => {
            const shareUrl = `${window.location.origin}/seller/${sellerProfile?.seller_id || sellerProfile?.user_id}`;
            const shareText = `Check out ${sellerProfile?.store_name || 'my store'} on EcoMart!`;
            
            if (navigator.share) {
              navigator.share({
                title: `${sellerProfile?.store_name || 'Store'} Profile`,
                text: shareText,
                url: shareUrl,
              });
            } else {
              navigator.clipboard.writeText(shareUrl)
                .then(() => alert('Store link copied to clipboard!'));
            }
          }}
          isMobile={true}
        />

          {/* Store Status */}
          <StoreStatusMobile />

          {/* Quick Stats */}
          <QuickStats 
            listings={listings}
            orders={orders}
            sellerStats={sellerStats}
            onNavigateToListings={() => navigate('/store/listings')}
            onNavigateToOrders={() => navigate('/store/orders')}
            onNavigateToAnalytics={() => navigate('/store/analytics')}
          />

          {/* Quick Actions */}
          <QuickActions listings={listings} />

          {/* Recent Listings */}
          <RecentListings listings={listings} />

          {/* Performance Tips */}
          <PerformanceTips />

          {/* Create Listing Button */}
          <div className="fixed bottom-20 right-4 z-40">
            <button
              onClick={() => navigate('/store/listings/create-listing')}
              className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-4 rounded-full font-semibold hover:from-orange-600 hover:to-red-600 transition flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Bottom Navigation */}
        <MobileBottomNav />
        <Footer />
      </div>
    );
  };

  /* ========== DESKTOP VIEW ========== */
  const DesktopView = () => {
    // Store Status Component for Desktop
    const StoreStatusDesktop = () => {
      if (!sellerProfile) return null;

      return (
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h3 className="text-lg font-bold text-black mb-4">Store Status</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Store Status</span>
                <span className={`px-3 py-1 text-xs rounded-full ${
                  sellerProfile.status === 'active' ? 'bg-green-100 text-green-800' :
                  sellerProfile.status === 'suspended' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {sellerProfile.status || 'Unknown'}
                </span>
              </div>
            </div>
            
            <div className="p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Approval Status</span>
                <span className={`px-3 py-1 text-xs rounded-full ${
                  sellerProfile.is_approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {sellerProfile.is_approved ? 'Approved' : 'Pending Review'}
                </span>
              </div>
            </div>
          </div>
          
          {!sellerProfile.is_approved && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-yellow-800 text-sm italic">
                ⏳ Your store is under review. You can still list items, but they won't be visible to buyers until approved.
              </p>
            </div>
          )}
        </div>
      );
    };

    // Store Setup Prompt for Desktop
    const StoreSetupPromptDesktop = () => (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-lg">
          <div className="w-20 h-20 mx-auto bg-gradient-to-r from-orange-100 to-red-100 rounded-full flex items-center justify-center mb-6">
            <svg className="w-10 h-10 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          
          <h1 className="text-2xl font-bold text-black mb-4">
            Ready to Start Selling? 🛍️
          </h1>
          
          <p className="text-gray-600 mb-8">
            Set up your store in just a few minutes and start reaching buyers on our marketplace
          </p>
          
          <div className="space-y-4 mb-8">
            {[
              'Upload store logo & cover photo',
              'Create your store profile',
              'Set delivery options & fees',
              'Start listing your products'
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-3">
                <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-gray-700 text-left text-base">{item}</span>
              </div>
            ))}
          </div>

          <div className="space-y-3">
            <button
              onClick={navigateToStoreSetup}
              className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 px-6 rounded-xl font-semibold hover:from-orange-600 hover:to-red-600 transition flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Setup My Store
            </button>

            <button
              onClick={() => setShowLaterModal(true)}
              className="w-full py-3 text-gray-600 hover:text-gray-800 transition text-base"
            >
              I'll do this later
            </button>
          </div>
        </div>
      </div>
    );

    // User not logged in - Desktop
    if (!user) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-lg">
            <div className="w-20 h-20 mx-auto bg-gradient-to-r from-orange-100 to-red-100 rounded-full flex items-center justify-center mb-6">
              <svg className="w-10 h-10 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            
            <h1 className="text-2xl font-bold text-black mb-4">
              Your Store Awaits! 🛍️
            </h1>
            
            <p className="text-gray-600 mb-8">
              Log in to access your store dashboard and start selling on our marketplace
            </p>
            
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 px-6 rounded-xl font-semibold hover:from-orange-600 hover:to-red-600 transition"
            >
              Log In to Your Store
            </button>
          </div>
        </div>
      );
    }

    // User is not a seller - Desktop
    if (!user.is_seller) {
      return (
        <>
          <StoreSetupPromptDesktop />
          <ConfirmationModal
            isOpen={showLaterModal}
            onClose={() => setShowLaterModal(false)}
            onConfirm={() => {
              setShowLaterModal(false);
              navigate("/");
            }}
            title="Setup Later"
            message="You can setup your store anytime from your profile. Some features will be limited until you complete store setup."
            confirmText="OK"
            cancelText={null}
            showCancel={false}
          />
        </>
      );
    }

    return (
      <div className="hidden lg:block min-h-screen bg-gray-50">
        {/* Desktop Header */}
        <Header />
        
        <main className="max-w-7xl mx-auto px-4 py-8">
          {/* Store Header */}
          <StoreHeader 
            sellerProfile={sellerProfile}
            onNavigateToProfile={navigateToStoreProfile}
            onNavigateToChats={() => navigate('/chats')}
            onShareStore={() => {
              const shareUrl = `${window.location.origin}/seller/${sellerProfile?.seller_id || sellerProfile?.user_id}`;
              const shareText = `Check out ${sellerProfile?.store_name || 'my store'} on EcoMart!`;
              
              if (navigator.share) {
                navigator.share({
                  title: `${sellerProfile?.store_name || 'Store'} Profile`,
                  text: shareText,
                  url: shareUrl,
                });
              } else {
                navigator.clipboard.writeText(shareUrl)
                  .then(() => alert('Store link copied to clipboard!'));
              }
            }}
            isMobile={false}
          />

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mt-6">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-3 space-y-6">
              {/* Store Status */}
              <StoreStatusDesktop />

              {/* Quick Stats */}
              <QuickStats 
    listings={listings}
    orders={orders}
    sellerStats={sellerStats}
    onNavigateToListings={() => navigate('/store/listings')}
    onNavigateToOrders={() => navigate('/store/orders')}
    onNavigateToAnalytics={() => navigate('/store/analytics')}
  />

              {/* Grid for Actions and Recent Listings */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Quick Actions */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <QuickActions listings={listings} />
                </div>

                {/* Recent Listings */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <RecentListings listings={listings} />
                </div>
              </div>
            </div>

            {/* Right Column - Performance Tips */}
            <div className="lg:col-span-1">
              <PerformanceTips />
            </div>
          </div>
        </main>

        {/* Create Listing Button */}
        <div className="fixed right-8 bottom-8 z-40">
          <button
            onClick={() => navigate('/store/listings/create-listing')}
            className="bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 px-6 rounded-xl font-semibold hover:from-orange-600 hover:to-red-600 transition flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Listing
          </button>
        </div>
        
        {/* Desktop Footer */}
        <Footer />
      </div>
    );
  };

  /* ========== MAIN RENDER LOGIC ========== */
  
  // Show loading while checking seller status
  if (checkingSellerStatus) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loading size="large" />
          <p className="mt-4 text-gray-600">Checking your store status...</p>
        </div>
      </div>
    );
  }

  // User is seller but data is loading
  if (loading && user?.is_seller) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loading size="large" />
          <p className="mt-4 text-gray-600">Loading your store dashboard...</p>
        </div>
      </div>
    );
  }

  // Render mobile and desktop views
  return (
    <>
      <MobileView />
      <DesktopView />
    </>
  );
}